// ═══════════════════════════════════════════════════════════════
// ALLOYHEAD CORE — Layer Factories + Adjustment Applicator
// ═══════════════════════════════════════════════════════════════
// NOTE: PB (ParamBuffer) and CR (CommandRing) were removed in RC2.1.
// Param read/write now uses @alloyhead/core ParamWriter/ParamReader via SAB.
// Command dispatch now uses @alloyhead/core RingProducer/RingConsumer via SAB.
import { ADJ } from './constants.js';

// Raster layer factory
export function mkL(name, w, h, fillWhite) {
  const c = new OffscreenCanvas(w, h);
  const mc = new OffscreenCanvas(w, h);
  const x = c.getContext("2d", { willReadFrequently: true });
  x.imageSmoothingEnabled = true; x.imageSmoothingQuality = "high";
  if(fillWhite){x.fillStyle="#fff";x.fillRect(0,0,w,h);}
  const mx = mc.getContext("2d", { willReadFrequently: true });
  mx.fillStyle = "#fff"; mx.fillRect(0, 0, w, h);
  return {
    name, c, x,
    vis: true, op: 1, blend: "source-over",
    fx: { shadow: null, bevel: null, outline: null, innerShadow: null, outerGlow: null, innerGlow: null, colorOverlay: null, gradOverlay: null },
    mask: mc, maskCtx: mx, maskEnabled: false, maskLinked: true,
    textData: null, type: "raster", adjType: null, adjParams: null,
  };
}

// Adjustment layer factory
export function mkAdjL(name, adjType, params, w, h) {
  const l = mkL(name, w, h);
  l.type = "adjustment"; l.adjType = adjType; l.adjParams = { ...params };
  return l;
}

// Group layer factory
export function mkGroupL(name, w, h) {
  const l = mkL(name, w, h);
  l.type = "group"; l.children = []; l.collapsed = false;
  return l;
}

// Vector layer factory — stores PathList data, renders by rasterizing paths
export function mkVectorL(name, w, h, pathList, style) {
  const l = mkL(name, w, h);
  l.type = "vector";
  l.vectorData = {
    paths: pathList || [],
    style: {
      strokeColor: style?.strokeColor || null,
      strokeWidth: style?.strokeWidth || 2,
      fillColor: style?.fillColor || null,
      opacity: style?.opacity ?? 1,
      ...(style || {}),
    },
  };
  l._vectorDirty = true;
  return l;
}

// ═══════════════════════════════════════════════════════════════
// CURVES LUT BUILDER (Fritsch-Carlson monotone cubic spline)
// ═══════════════════════════════════════════════════════════════
export function buildCurvesLUT(rawPts) {
  const pts = (rawPts || [[0, 0], [255, 255]]).slice().sort((a, b) => a[0] - b[0]);
  const lut = new Uint8Array(256);
  const n = pts.length;
  if (n < 2) { for (let i = 0; i < 256; i++) lut[i] = i; return lut; }
  const dx = [], dy = [], m = [];
  for (let k = 0; k < n - 1; k++) { dx[k] = pts[k + 1][0] - pts[k][0]; dy[k] = pts[k + 1][1] - pts[k][1]; m[k] = dx[k] === 0 ? 0 : dy[k] / dx[k]; }
  const tg = new Array(n);
  tg[0] = m[0]; tg[n - 1] = m[n - 2];
  for (let k = 1; k < n - 1; k++) {
    if (m[k - 1] * m[k] <= 0) tg[k] = 0;
    else tg[k] = (m[k - 1] + m[k]) / 2;
  }
  for (let k = 0; k < n - 1; k++) {
    if (dx[k] === 0) { tg[k] = 0; tg[k + 1] = 0; continue; }
    if (m[k] !== 0) { const a = tg[k] / m[k], b = tg[k + 1] / m[k], s2 = a * a + b * b; if (s2 > 9) { const tau = 3 / Math.sqrt(s2); tg[k] = tau * a * m[k]; tg[k + 1] = tau * b * m[k]; } }
  }
  for (let i = 0; i < 256; i++) {
    if (i <= pts[0][0]) { lut[i] = Math.max(0, Math.min(255, Math.round(pts[0][1]))); continue; }
    if (i >= pts[n - 1][0]) { lut[i] = Math.max(0, Math.min(255, Math.round(pts[n - 1][1]))); continue; }
    let k = 0; for (let j = 0; j < n - 1; j++) { if (i >= pts[j][0] && i <= pts[j + 1][0]) { k = j; break; } }
    const h = dx[k] || 1, t = (i - pts[k][0]) / h;
    const h00 = (1 + 2 * t) * (1 - t) * (1 - t), h10 = t * (1 - t) * (1 - t), h01 = t * t * (3 - 2 * t), h11 = t * t * (t - 1);
    const v = h00 * pts[k][1] + h10 * h * tg[k] + h01 * pts[k + 1][1] + h11 * h * tg[k + 1];
    lut[i] = Math.max(0, Math.min(255, Math.round(v)));
  }
  return lut;
}

// ═══════════════════════════════════════════════════════════════
// ADJUSTMENT APPLICATOR — applies adj layer params to ImageData in-place
// ═══════════════════════════════════════════════════════════════
export function applyAdjustment(id, adjType, params, bridge) {
  const d = id.data, len = d.length;
  // ── Wasm fast path — route through compiled kernels when bridge available ──
  const B = bridge;
  if (B && B._wasmAvailable) {
    if (adjType === ADJ.BRIGHTNESS) { B.runKernel("brightness_contrast", d, 0, 0, { brightness: params.brightness || 0, contrast: params.contrast || 0 }); return; }
    if (adjType === ADJ.LEVELS) { B.runKernel("levels", d, 0, 0, params); return; }
    if (adjType === ADJ.HUESAT) { B.runKernel("hue_saturation", d, 0, 0, { hue: params.hue || 0, saturation: params.saturation || 0, lightness: params.lightness || 0 }); return; }
    if (adjType === ADJ.INVERT) { B.runKernel("invert", d, 0, 0); return; }
    // Curves and ColorBalance fall through to JS (complex LUT/zone logic)
  }
  // ── JS fallback ──
  if (adjType === ADJ.BRIGHTNESS) {
    const br = (params.brightness || 0) / 100 * 255 * 0.5;
    const ct = (params.contrast || 0) / 100;
    const factor = (1 + ct) * (1 + ct);
    const lut = new Uint8Array(256);
    for (let i = 0; i < 256; i++) { let v = ((i + br) / 255 - 0.5) * factor + 0.5; lut[i] = v < 0 ? 0 : v > 1 ? 255 : (v * 255 + 0.5) | 0; }
    for (let i = 0; i < len; i += 4) { d[i] = lut[d[i]]; d[i + 1] = lut[d[i + 1]]; d[i + 2] = lut[d[i + 2]]; }
  } else if (adjType === ADJ.LEVELS) {
    const iB = params.inBlack || 0, iW = params.inWhite || 255, oB = params.outBlack || 0, oW = params.outWhite || 255, gm = params.gamma || 1;
    const range = iW - iB || 1;
    const lut = new Uint8Array(256);
    for (let i = 0; i < 256; i++) { let v = (i - iB) / range; v = v < 0 ? 0 : v > 1 ? 1 : v; v = Math.pow(v, 1 / gm); lut[i] = (oB + v * (oW - oB) + 0.5) | 0; }
    for (let i = 0; i < len; i += 4) { d[i] = lut[d[i]]; d[i + 1] = lut[d[i + 1]]; d[i + 2] = lut[d[i + 2]]; }
  } else if (adjType === ADJ.HUESAT) {
    const hShift = (params.hue || 0) / 360, sScale = 1 + (params.saturation || 0) / 100, lShift = (params.lightness || 0) / 100 * 0.5;
    for (let i = 0; i < len; i += 4) {
      if (d[i + 3] < 1) continue;
      let r = d[i] / 255, g = d[i + 1] / 255, b = d[i + 2] / 255;
      const mx = Math.max(r, g, b), mn = Math.min(r, g, b), dl = mx - mn;
      let h2 = 0, s2 = 0, l2 = (mx + mn) / 2;
      if (dl > 0) { s2 = l2 > 0.5 ? dl / (2 - mx - mn) : dl / (mx + mn); if (mx === r) h2 = ((g - b) / dl + (g < b ? 6 : 0)) / 6; else if (mx === g) h2 = ((b - r) / dl + 2) / 6; else h2 = ((r - g) / dl + 4) / 6; }
      h2 = (h2 + hShift) % 1; if (h2 < 0) h2 += 1;
      s2 = s2 * sScale; if (s2 < 0) s2 = 0; if (s2 > 1) s2 = 1;
      l2 = l2 + lShift; if (l2 < 0) l2 = 0; if (l2 > 1) l2 = 1;
      if (s2 === 0) { r = g = b = l2; } else {
        const hue2rgb = (p, q, t) => { if (t < 0) t += 1; if (t > 1) t -= 1; if (t < 1 / 6) return p + (q - p) * 6 * t; if (t < 1 / 2) return q; if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6; return p; };
        const q2 = l2 < 0.5 ? l2 * (1 + s2) : l2 + s2 - l2 * s2, p2 = 2 * l2 - q2;
        r = hue2rgb(p2, q2, h2 + 1 / 3); g = hue2rgb(p2, q2, h2); b = hue2rgb(p2, q2, h2 - 1 / 3);
      }
      d[i] = (r * 255 + 0.5) | 0; d[i + 1] = (g * 255 + 0.5) | 0; d[i + 2] = (b * 255 + 0.5) | 0;
    }
  } else if (adjType === ADJ.INVERT) {
    for (let i = 0; i < len; i += 4) { d[i] = 255 - d[i]; d[i + 1] = 255 - d[i + 1]; d[i + 2] = 255 - d[i + 2]; }
  } else if (adjType === ADJ.CURVES) {
    const lut = buildCurvesLUT(params.points);
    const ch = params.channel || "master";
    if (ch === "master") { for (let i = 0; i < len; i += 4) { if (d[i + 3] < 1) continue; d[i] = lut[d[i]]; d[i + 1] = lut[d[i + 1]]; d[i + 2] = lut[d[i + 2]]; } }
    else if (ch === "r") { for (let i = 0; i < len; i += 4) d[i] = lut[d[i]]; }
    else if (ch === "g") { for (let i = 0; i < len; i += 4) d[i + 1] = lut[d[i + 1]]; }
    else if (ch === "b") { for (let i = 0; i < len; i += 4) d[i + 2] = lut[d[i + 2]]; }
  } else if (adjType === ADJ.COLORBAL) {
    const sR = (params.shadowR || 0) / 100, sG = (params.shadowG || 0) / 100, sB = (params.shadowB || 0) / 100;
    const mR = (params.midR || 0) / 100, mG = (params.midG || 0) / 100, mB = (params.midB || 0) / 100;
    const hR = (params.highR || 0) / 100, hG = (params.highG || 0) / 100, hB = (params.highB || 0) / 100;
    const pLum = params.preserveLum !== false;
    for (let i = 0; i < len; i += 4) {
      if (d[i + 3] < 1) continue;
      const lum = d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
      let sw, mw, hw;
      if (lum <= 85) { sw = 1; mw = 0; hw = 0; const t = lum / 85; mw = 0.5 * (1 - Math.cos(t * Math.PI)); sw = 1 - mw; }
      else if (lum <= 170) { sw = 0; hw = 0; const t = (lum - 85) / 85; hw = 0.5 * (1 - Math.cos(t * Math.PI)); mw = 1 - hw; }
      else { sw = 0; mw = 0; hw = 1; }
      let r = d[i] + (sR * sw + mR * mw + hR * hw) * 255;
      let g = d[i + 1] + (sG * sw + mG * mw + hG * hw) * 255;
      let b = d[i + 2] + (sB * sw + mB * mw + hB * hw) * 255;
      if (pLum) { const newLum = r * 0.299 + g * 0.587 + b * 0.114; if (newLum > 0) { const sc = lum / newLum; r *= sc; g *= sc; b *= sc; } }
      d[i] = r < 0 ? 0 : r > 255 ? 255 : (r + 0.5) | 0;
      d[i + 1] = g < 0 ? 0 : g > 255 ? 255 : (g + 0.5) | 0;
      d[i + 2] = b < 0 ? 0 : b > 255 ? 255 : (b + 0.5) | 0;
    }
  }
  // ════════════════ Phase 6: Color Grading ════════════════
  else if (adjType === ADJ.VIBRANCE) {
    const amt = (params.amount || 0) / 100;
    if (amt === 0) return;
    for (let i = 0; i < len; i += 4) {
      if (d[i + 3] < 1) continue;
      const r = d[i], g = d[i + 1], b = d[i + 2];
      const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
      const sat = mx === 0 ? 0 : (mx - mn) / mx;
      const boost = amt * (1 - sat);
      const avg = (r + g + b) / 3;
      d[i] = Math.max(0, Math.min(255, Math.round(r + (r - avg) * boost)));
      d[i + 1] = Math.max(0, Math.min(255, Math.round(g + (g - avg) * boost)));
      d[i + 2] = Math.max(0, Math.min(255, Math.round(b + (b - avg) * boost)));
    }
  }
  else if (adjType === ADJ.CHANNEL_MIX) {
    // RGB matrix transform: each output channel = weighted sum of inputs
    const rr = (params.rr ?? 100) / 100, rg = (params.rg ?? 0) / 100, rb = (params.rb ?? 0) / 100;
    const gr = (params.gr ?? 0) / 100, gg = (params.gg ?? 100) / 100, gb = (params.gb ?? 0) / 100;
    const br = (params.br ?? 0) / 100, bg = (params.bg ?? 0) / 100, bb = (params.bb ?? 100) / 100;
    for (let i = 0; i < len; i += 4) {
      if (d[i + 3] < 1) continue;
      const r = d[i], g2 = d[i + 1], b2 = d[i + 2];
      d[i] = Math.max(0, Math.min(255, (r * rr + g2 * rg + b2 * rb + 0.5) | 0));
      d[i + 1] = Math.max(0, Math.min(255, (r * gr + g2 * gg + b2 * gb + 0.5) | 0));
      d[i + 2] = Math.max(0, Math.min(255, (r * br + g2 * bg + b2 * bb + 0.5) | 0));
    }
  }
  else if (adjType === ADJ.BW_MIX) {
    // Custom B&W: weighted channel contribution (simulates color lens filters)
    const rw = (params.red ?? 40) / 100, gw = (params.green ?? 34) / 100, bw = (params.blue ?? 26) / 100;
    const total = Math.abs(rw) + Math.abs(gw) + Math.abs(bw) || 1;
    const nr = rw / total, ng = gw / total, nb = bw / total;
    for (let i = 0; i < len; i += 4) {
      if (d[i + 3] < 1) continue;
      const v = Math.max(0, Math.min(255, (d[i] * nr + d[i + 1] * ng + d[i + 2] * nb + 0.5) | 0));
      d[i] = d[i + 1] = d[i + 2] = v;
    }
  }
  else if (adjType === ADJ.SELECTIVE) {
    // Selective Color: adjust CMYK within a specific hue range
    const range = params.range || "reds";
    const cAdj = (params.cyan || 0) / 100, mAdj = (params.magenta || 0) / 100;
    const yAdj = (params.yellow || 0) / 100, kAdj = (params.black || 0) / 100;
    // Hue ranges (degrees): which pixels to affect
    const RANGES = {
      reds: [345, 15], yellows: [15, 75], greens: [75, 165],
      cyans: [165, 195], blues: [195, 285], magentas: [285, 345],
      neutrals: null, whites: null, blacks: null,
    };
    const hr = RANGES[range];
    for (let i = 0; i < len; i += 4) {
      if (d[i + 3] < 1) continue;
      const r = d[i], g2 = d[i + 1], b2 = d[i + 2];
      const mx = Math.max(r, g2, b2), mn = Math.min(r, g2, b2);
      const lum = (mx + mn) / 2;
      let weight = 0;
      if (range === "neutrals") {
        const sat = mx === 0 ? 0 : (mx - mn) / mx;
        weight = sat < 0.15 ? 1 : 0;
      } else if (range === "whites") {
        weight = lum > 200 ? (lum - 200) / 55 : 0;
      } else if (range === "blacks") {
        weight = lum < 55 ? (55 - lum) / 55 : 0;
      } else if (hr) {
        let hue = 0;
        if (mx - mn > 2) {
          if (mx === r) hue = ((g2 - b2) / (mx - mn)) * 60;
          else if (mx === g2) hue = ((b2 - r) / (mx - mn)) * 60 + 120;
          else hue = ((r - g2) / (mx - mn)) * 60 + 240;
          if (hue < 0) hue += 360;
        }
        if (hr[0] > hr[1]) weight = (hue >= hr[0] || hue <= hr[1]) ? 1 : 0; // wraps around 360
        else weight = (hue >= hr[0] && hue <= hr[1]) ? 1 : 0;
      }
      if (weight <= 0) continue;
      // Apply CMYK adjustments (additive model approximation)
      d[i] = Math.max(0, Math.min(255, r + (-cAdj + kAdj * -1) * weight * 255));
      d[i + 1] = Math.max(0, Math.min(255, g2 + (-mAdj + kAdj * -1) * weight * 255));
      d[i + 2] = Math.max(0, Math.min(255, b2 + (-yAdj + kAdj * -1) * weight * 255));
    }
  }
  else if (adjType === ADJ.GRAD_MAP) {
    // Gradient Map: map luminance to color gradient
    const stops = params.stops || [{pos: 0, color: "#000000"}, {pos: 1, color: "#ffffff"}];
    if (stops.length < 2) return;
    const sorted = stops.slice().sort((a, b) => a.pos - b.pos);
    // Parse stop colors
    const parsed = sorted.map(s => {
      const h = (s.color || "#000000").replace("#", "");
      return { pos: s.pos, r: parseInt(h.slice(0, 2), 16) || 0, g: parseInt(h.slice(2, 4), 16) || 0, b: parseInt(h.slice(4, 6), 16) || 0 };
    });
    // Build 256-entry LUT
    const lutR = new Uint8Array(256), lutG = new Uint8Array(256), lutB = new Uint8Array(256);
    for (let i = 0; i < 256; i++) {
      const t = i / 255;
      // Find surrounding stops
      let lo = parsed[0], hi = parsed[parsed.length - 1];
      for (let j = 0; j < parsed.length - 1; j++) {
        if (t >= parsed[j].pos && t <= parsed[j + 1].pos) { lo = parsed[j]; hi = parsed[j + 1]; break; }
      }
      const range = hi.pos - lo.pos || 1;
      const f = (t - lo.pos) / range;
      lutR[i] = (lo.r + (hi.r - lo.r) * f + 0.5) | 0;
      lutG[i] = (lo.g + (hi.g - lo.g) * f + 0.5) | 0;
      lutB[i] = (lo.b + (hi.b - lo.b) * f + 0.5) | 0;
    }
    for (let i = 0; i < len; i += 4) {
      if (d[i + 3] < 1) continue;
      const lum = Math.max(0, Math.min(255, (d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114 + 0.5) | 0));
      d[i] = lutR[lum]; d[i + 1] = lutG[lum]; d[i + 2] = lutB[lum];
    }
  }
  else if (adjType === ADJ.SHADOW_HL) {
    // Shadow/Highlight Recovery: tone-targeted brightness
    const shAmt = (params.shadows || 0) / 100;
    const hlAmt = (params.highlights || 0) / 100;
    const tw = Math.max(10, Math.min(100, params.tonalWidth ?? 50)) / 100;
    if (shAmt === 0 && hlAmt === 0) return;
    const shThresh = 85 * tw, hlThresh = 255 - 85 * tw;
    for (let i = 0; i < len; i += 4) {
      if (d[i + 3] < 1) continue;
      const lum = d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
      let adj = 0;
      if (lum < shThresh && shAmt !== 0) {
        const w = (shThresh - lum) / shThresh; // 1 at black, 0 at threshold
        adj += shAmt * w * 128; // max boost 128 levels
      }
      if (lum > hlThresh && hlAmt !== 0) {
        const w = (lum - hlThresh) / (255 - hlThresh);
        adj -= hlAmt * w * 128; // negative = darken highlights
      }
      if (adj !== 0) {
        d[i] = Math.max(0, Math.min(255, (d[i] + adj + 0.5) | 0));
        d[i + 1] = Math.max(0, Math.min(255, (d[i + 1] + adj + 0.5) | 0));
        d[i + 2] = Math.max(0, Math.min(255, (d[i + 2] + adj + 0.5) | 0));
      }
    }
  }
}
